import { useRef, useState } from 'react';

interface Props {
  label: string;
  width: number;
  align: 'left' | 'center' | 'right';
  thClassName?: string;
  /** 드롭다운을 열 때 계산 — 다른 칼럼 필터가 이미 반영된 "이 칼럼의 선택 가능한 값" 목록 */
  getAllValues: () => string[];
  /** undefined/전체선택 = 필터 없음 */
  selected: Set<string> | undefined;
  sortDir: 'asc' | 'desc' | null;
  onApplyFilter: (values: Set<string> | null) => void;
  onSetSort: (dir: 'asc' | 'desc' | null) => void;
}

/**
 * 상세표/차단기 팝업 공용 칼럼 헤더 셀 — 값 필터(체크박스+검색) + 정렬(오름/내림/해제) 드롭다운.
 * HTML 툴(UT_Matrix_비교분석_Rev15.html)의 컬럼 필터 드롭다운(#cfd)을 React 컴포넌트로 포팅.
 */
const ColumnFilterDropdown = ({
  label,
  width,
  align,
  thClassName,
  getAllValues,
  selected,
  sortDir,
  onApplyFilter,
  onSetSort,
}: Props) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [draft, setDraft] = useState<Set<string> | null>(null);
  const [allValues, setAllValues] = useState<string[]>([]);
  const [pos, setPos] = useState({ left: 0, top: 0 });
  const arrowRef = useRef<HTMLSpanElement>(null);

  const hasFilter = Boolean(selected && selected.size > 0);

  const openDropdown = () => {
    const vals = getAllValues();
    setAllValues(vals);
    setDraft(selected ? new Set(selected) : new Set(vals));
    setQuery('');
    const rect = arrowRef.current?.getBoundingClientRect();
    if (rect) {
      let left = rect.left;
      let top = rect.bottom + 4;
      if (left + 260 > window.innerWidth) left = window.innerWidth - 264;
      if (top + 360 > window.innerHeight) top = rect.top - 364;
      setPos({ left, top });
    }
    setOpen(true);
  };

  const visibleVals = query
    ? allValues.filter((v) => v.toLowerCase().includes(query.toLowerCase()))
    : allValues;

  const apply = () => {
    if (!draft) return setOpen(false);
    if (draft.size === allValues.length) onApplyFilter(null);
    else onApplyFilter(new Set(draft));
    setOpen(false);
  };

  const toggleVal = (v: string) =>
    setDraft((prev) => {
      const next = new Set(prev ?? []);
      if (next.has(v)) next.delete(v);
      else next.add(v);
      return next;
    });

  return (
    <th
      className={`col-hdr${thClassName ? ` ${thClassName}` : ''}`}
      style={{ width, minWidth: width, textAlign: align }}
    >
      <div className="th-cell">
        <span className="th-label" title={label}>
          {label}
        </span>
        <span
          ref={arrowRef}
          className={`th-arrow${hasFilter ? ' has-filter' : ''}${
            sortDir ? ` sorted-${sortDir}` : ''
          }`}
          onClick={(e) => {
            e.stopPropagation();
            openDropdown();
          }}
        >
          {hasFilter ? '▼●' : '▼'}
        </span>
      </div>
      {open && (
        <>
          <div className="cfd-backdrop" onClick={() => setOpen(false)} />
          <div className="cfd" style={{ left: pos.left, top: pos.top }}>
            <div className="cfd-hdr">
              <b>{label}</b>
              <button type="button" onClick={() => setOpen(false)}>
                ✕
              </button>
            </div>
            <div className="cfd-sort">
              <button
                type="button"
                className={`cfd-sort-btn${sortDir === 'asc' ? ' active' : ''}`}
                onClick={() => {
                  onSetSort('asc');
                  setOpen(false);
                }}
              >
                ▲ 오름차순
              </button>
              <button
                type="button"
                className={`cfd-sort-btn${sortDir === 'desc' ? ' active' : ''}`}
                onClick={() => {
                  onSetSort('desc');
                  setOpen(false);
                }}
              >
                ▼ 내림차순
              </button>
              <button
                type="button"
                className="cfd-sort-btn cfd-sort-clear"
                onClick={() => {
                  onSetSort(null);
                  setOpen(false);
                }}
              >
                해제
              </button>
            </div>
            <div className="cfd-search">
              <input
                type="text"
                placeholder="검색…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                autoFocus
              />
            </div>
            <div className="cfd-acts">
              <button
                type="button"
                onClick={() => setDraft((prev) => new Set([...(prev ?? []), ...visibleVals]))}
              >
                전체 선택
              </button>
              <button
                type="button"
                onClick={() =>
                  setDraft((prev) => {
                    const next = new Set(prev ?? []);
                    visibleVals.forEach((v) => next.delete(v));
                    return next;
                  })
                }
              >
                전체 해제
              </button>
            </div>
            <div className="cfd-list">
              {visibleVals.map((v) => (
                <label key={v} className="cfd-item">
                  <input
                    type="checkbox"
                    checked={draft?.has(v) ?? false}
                    onChange={() => toggleVal(v)}
                  />
                  <span>{v || '(빈값)'}</span>
                </label>
              ))}
            </div>
            <div className="cfd-foot">
              <button type="button" className="cfd-cancel" onClick={() => setOpen(false)}>
                취소
              </button>
              <button type="button" className="cfd-ok" onClick={apply}>
                적용
              </button>
            </div>
          </div>
        </>
      )}
    </th>
  );
};

export default ColumnFilterDropdown;
